import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo } from "react";
import { Typography, makeStyles, FormHelperText, Grid } from "@material-ui/core";
import clsx from "clsx";
import { FormattedNumber } from "react-intl";
import { identityRecordOfFieldEditorOptions, getFieldError, defaultGetItemLabel, defaultGetItemValue } from "@ploy-ui/form-fields";
import merge from "lodash/merge";
const useStyles = makeStyles(theme => ({
    wrapper: {
        width: "100%"
    },
    root: {
        display: "flex",
        alignItems: "baseline",
        width: "100%",
        margin: 0
    },
    multiple: {
        alignItems: "flex-end"
    },
    label: {
        flexGrow: 0,
        marginRight: ({ textAlign }) => Boolean(textAlign) ? 0 : theme.spacing(1),
        whiteSpace: "pre-line"
    },
    value: {
        flexGrow: 1,
        textAlign: ({ textAlign = "right" }) => textAlign,
        whiteSpace: "pre-line",
        marginLeft: ({ textAlign = "right" }) => textAlign === "left" ? 5 : 0
    },
    alignment: {
        textAlign: ({ textAlign }) => textAlign,
        width: "100%"
    },
    suffix: {
        marginLeft: theme.spacing(1),
        flexGrow: 0,
        width: 20,
        whiteSpace: "nowrap"
    },
    marginNormal: {
        marginTop: 0,
        marginBottom: theme.spacing(1)
    },
    marginDense: {
        marginTop: 8,
        marginBottom: 4
    },
    center: { textAlign: "center" },
    variantContained: {
        padding: theme.spacing(1),
        background: theme.palette.grey[300],
        "& > $value": {
            fontSize: 12,
            textAlign: "inherit"
        }
    },
    variantInformation: {
        borderLeft: "6px solid " + theme.palette.primary.main,
        fontSize: 16
    },
    variantInformationLabel: {
        paddingLeft: theme.spacing(1)
    },
    variantNote: {
        borderLeft: "6px solid " + theme.palette.primary.main,
        paddingLeft: theme.spacing(1),
        fontSize: 16
    },
    variantEmphasized: {
        // borderBottom: "1px solid #efefef",
        "& > $value": {
            fontWeight: 700
        }
    },
    variantPlain: {
        marginBottom: 0,
        alignItems: "center"
    },
    variantNormal: {
        "& > $value": {
            fontSize: 12,
            textAlign: "inherit"
        }
    },
    variantWarning: {
        color: "orange"
    },
    variantBig: {},
    bigVariantValue: {
        fontSize: "2.5rem"
    }
}), { name: "DployFormLiteral" });
const literalVariants = [
    "big",
    "contained",
    "emphasized",
    "information",
    "normal",
    "note",
    "plain",
    "warning",
    "none"
];
export function getLiteralBaseProps(props) {
    const { options, form: { errors, touched }, field, helperText, errorDisplay, ...rest } = props;
    return {
        ...rest,
        ...options,
        label: rest.label,
        ...getFieldError(errors, touched, field.name, errorDisplay)
    };
}
export function LiteralBaseInternal(props) {
    const { className, suffix, prefix, margin, children, error, helperText, multiple, literalVariant = "emphasized" } = props;
    const classes = useStyles(props);
    let label = props.label;
    let hideValue = false;
    // Label can be both a component and a string, and it can also be set to " " when using `hiddenLabel`, so the following check must trim the label if it's a string
    if ((!label || (typeof label === "string" && label?.trim() === "")) &&
        typeof children === "string") {
        label = children.toString().trim();
        hideValue = true;
    }
    const styleParameters = merge({}, props.boldText ? { fontWeight: "bold" } : {}, props.italicText ? { fontStyle: "italic" } : {}, props.fontSize && !children ? { fontSize: props.fontSize } : {} // Only apply fontSize-style to field's label if field has no value
    );
    const valueStyleParams = merge({}, props.boldText ? { fontWeight: "bold" } : {}, props.italicText ? { fontStyle: "italic" } : {}, props.fontSize ? { fontSize: props.fontSize } : {});
    if (literalVariant === "big") {
        return (_jsxs(Grid, { container: true, className: clsx(classes.wrapper), children: [_jsxs(Grid, { container: true, item: true, xs: 12, className: clsx(className, classes.root, classes.center, {
                        [classes.variantBig]: literalVariant === "big",
                        [classes.marginNormal]: margin === "normal",
                        [classes.marginDense]: margin === "dense"
                    }), children: [label && (_jsx(Grid, { item: true, xs: 12, children: _jsx(Typography, { className: clsx(classes.label, {
                                    [classes.alignment]: props.hiddenLabel && props.textAlign
                                }), style: styleParameters, children: label }) })), !hideValue && (_jsx(Grid, { item: true, xs: 12, children: _jsxs(Typography, { variant: "h3", component: "p", className: clsx(classes.value, classes.bigVariantValue), style: valueStyleParams, children: [_jsx("span", { children: children }), (suffix || prefix) && (_jsx("span", { className: classes.suffix, style: valueStyleParams, children: suffix || prefix }))] }) }))] }), helperText && (_jsx(Grid, { item: true, xs: 12, children: _jsx(FormHelperText, { className: classes.center, error: error, children: helperText }) }))] }));
    }
    return (_jsxs("div", { className: clsx(classes.wrapper), children: [_jsxs(Typography, { paragraph: true, className: clsx(className, classes.root, {
                    [classes.variantEmphasized]: literalVariant === "emphasized",
                    [classes.variantPlain]: literalVariant === "plain",
                    [classes.variantContained]: literalVariant === "contained",
                    [classes.variantInformation]: literalVariant === "information",
                    [classes.variantNormal]: literalVariant === "normal",
                    [classes.variantNote]: literalVariant === "note",
                    [classes.variantWarning]: literalVariant === "warning",
                    [classes.marginNormal]: margin === "normal",
                    [classes.marginDense]: margin === "dense",
                    [classes.multiple]: multiple
                }), children: [label && (_jsx("span", { className: clsx(classes.label, {
                            [classes.alignment]: props.hiddenLabel && props.textAlign,
                            [classes.variantInformationLabel]: literalVariant === "information"
                        }), style: styleParameters, children: label })), !hideValue && (_jsx("span", { className: classes.value, style: valueStyleParams, children: children })), _jsx("span", { className: classes.suffix, style: valueStyleParams, children: suffix || prefix })] }), helperText && (_jsx(FormHelperText, { error: error, children: helperText }))] }));
}
LiteralBaseInternal.displayName = "LiteralBase";
export const LiteralBase = memo(LiteralBaseInternal);
export const baseEditableOptions = {
    variant: literalVariants
};
export const TextLiteral = (props) => {
    const { items = [], getItemValue = defaultGetItemValue, getItemLabel = defaultGetItemLabel, multiple } = props;
    let value = props.field.value;
    if (multiple) {
        value =
            value
                ?.map((v) => getItemLabel(items?.find(x => getItemValue(x) === v)) || v)
                .join("\n") || (Number(value) === 0 ? "" : value);
    }
    else {
        value =
            getItemLabel(items?.find(x => getItemValue(x) === value))?.split("|")[0] || (Number(value) === 0 ? "" : value);
    }
    return (_jsx(LiteralBase, { ...getLiteralBaseProps(props), children: typeof value === "number" ? (_jsx(FormattedNumber, { maximumFractionDigits: 0, value: value })) : (value) }));
};
TextLiteral.displayName = "TextLiteral";
export const EditorTextLiterals = identityRecordOfFieldEditorOptions({
    TextLiteral: {
        editableOptions: {
            ...baseEditableOptions
        }
    }
});
//# sourceMappingURL=TextLiteral.js.map